import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AudioPlaybackState {
  audioElement: HTMLAudioElement;
  isPlaying: boolean;
  duration: number;
  currentTime: number;
  waveformAnimation: any;
}

@Injectable({
  providedIn: 'root'
})
export class WhatsAppAudioPlaybackService {
  private playbackState = new Map<string, AudioPlaybackState>();
  private stateUpdates$ = new BehaviorSubject<Map<string, AudioPlaybackState>>(new Map());

  /**
   * Gets the message key for a message object
   */
  getMessageKey(message: any): string {
    return message.id || message.messageId || `${message.timestamp}-${message.from || message.to}`;
  }

  /**
   * Gets or creates audio element for a message
   */
  getAudioElement(message: any, messageKey?: string): HTMLAudioElement | null {
    const key = messageKey || this.getMessageKey(message);
    const state = this.playbackState.get(key);
    
    if (state && state.audioElement) {
      return state.audioElement;
    }
    
    // Try to find audio element by ID first, then by src
    const audioId = `audio-${key}`;
    let audioEl = document.getElementById(audioId) as HTMLAudioElement;
    
    if (!audioEl && message.mediaUrl) {
      // Fallback: find by src
      const audioElements = document.querySelectorAll(`audio[src="${message.mediaUrl}"]`);
      if (audioElements.length > 0) {
        audioEl = audioElements[0] as HTMLAudioElement;
      }
    }
    
    if (audioEl) {
      this.initializeAudio(key, audioEl);
      return audioEl;
    }
    
    return null;
  }

  /**
   * Initializes audio state for a message
   */
  initializeAudio(messageKey: string, audioElement: HTMLAudioElement): void {
    if (!this.playbackState.has(messageKey)) {
      this.playbackState.set(messageKey, {
        audioElement,
        isPlaying: false,
        duration: audioElement.duration || 0,
        currentTime: audioElement.currentTime || 0,
        waveformAnimation: null
      });
      this.notifyStateChange();
    }
  }

  /**
   * Gets playback state for a message
   */
  getState(message: any, messageKey?: string): AudioPlaybackState | null {
    const key = messageKey || this.getMessageKey(message);
    return this.playbackState.get(key) || null;
  }

  /**
   * Updates audio metadata (duration, current time)
   */
  updateMetadata(messageKey: string, duration: number, currentTime: number): void {
    const state = this.playbackState.get(messageKey);
    if (state) {
      state.duration = duration;
      state.currentTime = currentTime;
      this.notifyStateChange();
    }
  }

  /**
   * Updates playback progress
   */
  updateProgress(messageKey: string, currentTime: number, duration: number, isPlaying: boolean): void {
    let state = this.playbackState.get(messageKey);
    
    if (!state) {
      // Try to find audio element
      const audioEl = document.getElementById(`audio-${messageKey}`) as HTMLAudioElement;
      if (audioEl) {
        this.initializeAudio(messageKey, audioEl);
        state = this.playbackState.get(messageKey)!;
      } else {
        return;
      }
    }
    
    state.currentTime = currentTime;
    state.duration = duration || state.duration; // Keep existing duration if new one is 0
    state.isPlaying = isPlaying;
    this.notifyStateChange();
  }

  /**
   * Pauses all audio elements except the one with the specified key
   */
  pauseAllOthers(exceptKey: string): void {
    this.playbackState.forEach((state, key) => {
      if (key !== exceptKey && state.audioElement && !state.audioElement.paused) {
        state.audioElement.pause();
        state.isPlaying = false;
      }
    });
    this.notifyStateChange();
  }

  /**
   * Toggles playback for a message
   */
  togglePlayback(message: any, messageKey?: string): void {
    const key = messageKey || this.getMessageKey(message);
    const audioElement = this.getAudioElement(message, key);
    
    if (!audioElement) {
      console.warn('[AUDIO] Audio element not found for message:', key);
      return;
    }
    
    // Ensure state exists
    if (!this.playbackState.has(key)) {
      this.initializeAudio(key, audioElement);
    }
    
    const state = this.playbackState.get(key)!;
    
    if (state.isPlaying) {
      audioElement.pause();
      state.isPlaying = false;
    } else {
      audioElement.play().catch(error => {
        console.error('[AUDIO] Error playing audio:', error);
        state.isPlaying = false;
      });
      state.isPlaying = true;
    }
    
    this.notifyStateChange();
  }

  /**
   * Handles audio ended event
   */
  onAudioEnded(messageKey: string): void {
    const state = this.playbackState.get(messageKey);
    if (state) {
      state.isPlaying = false;
      state.currentTime = 0;
      this.notifyStateChange();
    }
  }

  /**
   * Handles audio error event
   */
  onAudioError(messageKey: string): void {
    const state = this.playbackState.get(messageKey);
    if (state) {
      state.isPlaying = false;
      this.notifyStateChange();
    }
  }

  /**
   * Checks if audio is currently playing
   */
  isPlaying(message: any, messageKey?: string): boolean {
    const key = messageKey || this.getMessageKey(message);
    const state = this.playbackState.get(key);
    const audioElement = this.getAudioElement(message, key);
    
    if (audioElement) {
      const isActuallyPlaying = !audioElement.paused && !audioElement.ended && audioElement.currentTime >= 0;
      if (state) {
        state.isPlaying = isActuallyPlaying;
      }
      return isActuallyPlaying;
    }
    
    return state?.isPlaying || false;
  }

  /**
   * Gets current playback progress (0-100)
   */
  getProgress(message: any, messageKey?: string): number {
    const key = messageKey || this.getMessageKey(message);
    const state = this.playbackState.get(key);
    
    if (!state) {
      return 0;
    }
    
    const duration = state.duration || 0;
    const currentTime = state.currentTime || 0;
    
    return duration > 0 ? (currentTime / duration) * 100 : 0;
  }

  /**
   * Formats time duration as MM:SS
   */
  formatTime(seconds: number): string {
    if (seconds === 0 || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Gets formatted duration string (current / total)
   */
  getFormattedDuration(message: any, messageKey?: string): string {
    const key = messageKey || this.getMessageKey(message);
    const state = this.playbackState.get(key);
    
    if (!state) {
      return '0:00 / 0:00';
    }
    
    const currentTime = state.currentTime || 0;
    const duration = state.duration || 0;
    
    return `${this.formatTime(currentTime)} / ${this.formatTime(duration)}`;
  }

  /**
   * Gets computed audio properties for a message
   */
  getComputedProperties(message: any): {
    isPlaying: boolean;
    progress: number;
    currentTime: number;
    duration: number;
    formattedDuration: string;
  } {
    const messageKey = this.getMessageKey(message);
    const state = this.getState(message, messageKey);
    const audioElement = this.getAudioElement(message, messageKey);
    
    // Compute isPlaying
    let isPlaying = false;
    if (audioElement) {
      isPlaying = !audioElement.paused && !audioElement.ended && audioElement.currentTime >= 0;
      if (state) {
        state.isPlaying = isPlaying;
      }
    } else if (state) {
      isPlaying = state.isPlaying;
    }
    
    // Compute progress
    let currentTime = state?.currentTime || 0;
    let duration = state?.duration || 0;
    
    if (audioElement) {
      if (duration === 0 && audioElement.duration && !isNaN(audioElement.duration)) {
        duration = audioElement.duration;
      }
      if (currentTime === 0 && audioElement.currentTime && !isNaN(audioElement.currentTime)) {
        currentTime = audioElement.currentTime;
      }
    }
    
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
    const formattedDuration = `${this.formatTime(currentTime)} / ${this.formatTime(duration)}`;
    
    return {
      isPlaying,
      progress,
      currentTime,
      duration,
      formattedDuration
    };
  }

  /**
   * Cleans up state for a message
   */
  cleanup(messageKey: string): void {
    this.playbackState.delete(messageKey);
    this.notifyStateChange();
  }

  /**
   * Cleans up all state
   */
  cleanupAll(): void {
    this.playbackState.clear();
    this.notifyStateChange();
  }

  /**
   * Notifies subscribers of state changes
   */
  private notifyStateChange(): void {
    this.stateUpdates$.next(new Map(this.playbackState));
  }

  /**
   * Observable for state updates (optional, for reactive updates)
   */
  getStateUpdates$(): Observable<Map<string, AudioPlaybackState>> {
    return this.stateUpdates$.asObservable();
  }
}

